/**
 * Video Trimmer — FFmpeg WASM-based client-side video trimming.
 * 
 * Used to trim Story video segments before uploading to Supabase Storage,
 * so each segment stays within Instagram's 61-second limit.
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// ─── Singleton FFmpeg instance ──────────────────────────────────────

let ffmpegInstance: FFmpeg | null = null;
let loadPromise: Promise<void> | null = null;

async function getFFmpeg(onProgress?: (msg: string) => void): Promise<FFmpeg> {
  if (ffmpegInstance?.loaded) return ffmpegInstance;

  if (loadPromise) {
    await loadPromise;
    return ffmpegInstance!;
  }

  ffmpegInstance = new FFmpeg();

  ffmpegInstance.on('log', ({ message }) => {
    if (import.meta.env.DEV) {
      console.log('[FFmpeg]', message);
    }
  });

  onProgress?.('Ładowanie silnika FFmpeg (~25 MB, jednorazowo)...');

  loadPromise = (async () => {
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
    await ffmpegInstance!.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
  })();

  await loadPromise;
  return ffmpegInstance!;
}

// ─── Public API ─────────────────────────────────────────────────────

export interface TrimResult {
  blob: Blob;
  durationSeconds: number;
}

export interface TrimProgress {
  segmentIndex: number;
  totalSegments: number;
  message: string;
}

/**
 * Trims a single segment from a video file.
 * 
 * @param videoSource - File/Blob (preferred) or public URL of the video
 * @param startSec   - Start time in seconds
 * @param endSec     - End time in seconds
 * @param onProgress - Progress callback
 * @returns Trimmed video Blob + actual duration
 */
export async function trimVideoSegment(
  videoSource: File | Blob | string,
  startSec: number,
  endSec: number,
  onProgress?: (msg: string) => void
): Promise<TrimResult> {
  const ffmpeg = await getFFmpeg(onProgress);

  onProgress?.(`Przygotowywanie wideo...`);

  // Read video data — prefer direct File/Blob to avoid re-downloading
  let videoData: Uint8Array;
  if (typeof videoSource !== 'string') {
    const arrayBuffer = await videoSource.arrayBuffer();
    videoData = new Uint8Array(arrayBuffer);
  } else {
    videoData = await fetchFile(videoSource);
  }

  const inputFile = `input.mp4`;
  const outputFile = `output.mp4`;

  // Write input
  await ffmpeg.writeFile(inputFile, videoData);

  const duration = endSec - startSec;

  onProgress?.(`Przycinanie (${formatTime(startSec)} → ${formatTime(endSec)})...`);

  // Trim with copy codec for speed (no re-encoding when possible)
  await ffmpeg.exec([
    '-ss', String(startSec),
    '-i', inputFile,
    '-t', String(duration),
    '-c', 'copy',
    '-movflags', '+faststart',
    '-y',
    outputFile,
  ]);

  // Read output
  const outputData = await ffmpeg.readFile(outputFile);
  // Cast to Uint8Array to satisfy BlobPart type (FFmpeg returns Uint8Array<ArrayBufferLike>)
  const blob = new Blob([new Uint8Array(outputData as Uint8Array)], { type: 'video/mp4' });

  // Cleanup
  await ffmpeg.deleteFile(inputFile);
  await ffmpeg.deleteFile(outputFile);

  return {
    blob,
    durationSeconds: Math.round(duration),
  };
}

/**
 * Trims multiple segments from a single video, one by one.
 * Reads the file once and reuses it for each segment.
 * 
 * @param videoSource - File/Blob (preferred) or URL
 */
export async function trimAllSegments(
  videoSource: File | Blob | string,
  segments: Array<{ startSeconds: number; endSeconds: number }>,
  onProgress?: (progress: TrimProgress) => void
): Promise<TrimResult[]> {
  const results: TrimResult[] = [];

  // If source is a URL, download once and convert to Blob
  let sourceBlob: Blob;
  if (typeof videoSource === 'string') {
    onProgress?.({ segmentIndex: 0, totalSegments: segments.length, message: 'Pobieranie wideo...' });
    const response = await fetch(videoSource);
    if (!response.ok) throw new Error(`Nie udało się pobrać wideo: ${response.status}`);
    sourceBlob = await response.blob();
  } else {
    sourceBlob = videoSource;
  }

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const result = await trimVideoSegment(
      sourceBlob,
      seg.startSeconds,
      seg.endSeconds,
      (msg) => onProgress?.({
        segmentIndex: i,
        totalSegments: segments.length,
        message: `Segment ${i + 1}/${segments.length}: ${msg}`,
      })
    );
    results.push(result);
  }

  return results;
}

// ─── Helpers ────────────────────────────────────────────────────────

function formatTime(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
